import React, { useState, useRef, useEffect } from "react";
import { Play, Mail, List } from "lucide-react";
import "../styles/Modal.css";

const WelcomeModal = ({ onClose }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [playVideo, setPlayVideo] = useState(false);
  const videoThumbnailRef = useRef(null);
  const iframeRef = useRef(null);

  // Add custom styles for iframe interaction
  useEffect(() => {
    if (playVideo) {
      const style = document.createElement("style");
      style.textContent = `
        .supademo-container iframe {
          -webkit-touch-callout: auto !important;
          -webkit-user-select: auto !important;
          touch-action: auto !important;
          user-select: auto !important;
          pointer-events: auto !important;
        }
        .supademo-container iframe * {
          pointer-events: auto !important;
        }
        .supademo-container {
          pointer-events: auto !important;
          touch-action: auto !important;
        }
        /* Make sure the container's children are clickable */
        .welcome-modal-content * {
          pointer-events: auto;
        }
        /* For small screens, ensure iframes can scroll */
        @media (max-width: 768px) {
          .supademo-container {
            overflow: visible !important;
            -webkit-overflow-scrolling: touch !important;
          }
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [playVideo]);

  const handleIframeAreaClick = (e) => {
    if (!iframeRef.current || !playVideo) return;
    if (window.innerWidth <= 768) {
      e.stopPropagation();
    }
  };

  const handleCheckboxChange = (e) => {
    setDontShowAgain(e.target.checked);
  };

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem("hideWelcomeModal", "true");
    }
    onClose();
  };

  const handleThumbnailClick = () => {
    setPlayVideo(true);
  };

  return (
    <div className="modal-backdrop">
      {/* 
        Use a NEW className="welcome-modal-content" 
        plus Tailwind classes for size & scroll 
      */}
      <div
        className="
        welcome-modal-content 
        bg-white 
        rounded-xl 
        shadow-2xl 
        w-full 
        max-w-2xl 
        mx-auto 
        transform 
        transition-all 
        max-h-[90vh] 
        overflow-y-auto 
        p-4 sm:p-6
        z-[10000]
      "
      >
        <h2 className="text-xl sm:text-2xl font-bold text-center mb-4 text-gray-800">
          Welcome to ExpenseBot
        </h2>

        {/* Video section with responsive design */}
        <div className="mb-4 rounded-lg overflow-hidden relative">
          {!playVideo ? (
            <div
              ref={videoThumbnailRef}
              onClick={handleThumbnailClick}
              className="relative bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg cursor-pointer group"
              style={{ aspectRatio: "1.7532656023222062" }}
            >
              {/* Email content */}
              <div className="flex flex-col items-center justify-center h-full p-6">
                <div className="bg-white rounded-lg shadow-lg p-4 mb-4 w-full max-w-sm mx-auto transform -rotate-2">
                  <div className="flex items-center mb-2">
                    <div className="h-3 w-3 rounded-full bg-red-500 mr-1"></div>
                    <div className="h-3 w-3 rounded-full bg-yellow-500 mr-1"></div>
                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="flex items-center mb-3">
                    <Mail className="text-blue-500 w-5 h-5 mr-2" />
                    <div className="text-gray-700 font-medium text-sm">
                      New Message
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="text-xs text-gray-500 mb-1">To:</div>
                    <div className="text-sm font-mono bg-blue-50 rounded px-2 py-1 text-blue-600">
                      receipts@expensebot.ai
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="text-xs text-gray-500 mb-1">Subject:</div>
                    <div className="text-sm font-medium">Lunch Receipt</div>
                  </div>
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="h-10 bg-gray-100 rounded flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                        />
                      </svg>
                      <span className="ml-1 text-xs text-gray-500">
                        receipt-attached.jpg
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center bg-blue-600 text-white px-3 py-1 rounded-full transform transition-transform hover:scale-105">
                  <Mail className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">
                    Auto-processed into spreadsheet
                  </span>
                </div>
              </div>

              {/* Prominent play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 group-hover:bg-opacity-30 transition-all">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-10 h-10 sm:w-12 sm:h-12 text-blue-600 ml-1" />
                </div>
              </div>

              {/* Title overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-900 to-transparent p-4">
                <h3 className="text-white font-medium text-lg">
                  Forward Receipts by Email
                </h3>
              </div>
            </div>
          ) : (
            // SupaDemo embed with aspect ratio container
            <div
              onClick={handleIframeAreaClick}
              style={{
                position: "relative",
                boxSizing: "content-box",
                maxHeight: "90vh",
                width: "100%",
                aspectRatio: "1.7532656023222062",
                touchAction: "auto",
              }}
              className="supademo-container"
            >
              <iframe
                ref={iframeRef}
                src="https://app.supademo.com/embed/cm90an0fo178zeyp0dlr2zpjh?embed_v=2&autoplay=true&mobile=true"
                loading="lazy"
                title="Email: Forward a Receipt"
                allow="clipboard-write; autoplay; fullscreen; web-share"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation-by-user-activation"
                frameBorder="0"
                webkitallowfullscreen="true"
                mozallowfullscreen="true"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: "0.5rem",
                  pointerEvents: "auto",
                  touchAction: "auto",
                  zIndex: 10,
                }}
                onLoad={() => setVideoLoaded(true)}
              ></iframe>
              {!videoLoaded && (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg"
                  style={{ aspectRatio: "1.7532656023222062" }}
                >
                  <div className="animate-pulse text-gray-500">
                    Loading demo...
                  </div>
                </div>
              )}
              <button
                onClick={() =>
                  window.open(
                    "https://app.supademo.com/embed/cm90an0fo178zeyp0dlr2zpjh?embed_v=2&autoplay=true",
                    "_blank"
                  )
                }
                className="absolute top-2 right-2 bg-blue-600 hover:bg-blue-700 text-white rounded p-2 transition-all z-20 shadow-md"
                title="Open in new window"
                aria-label="Open demo in new window"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </button>
            </div>
          )}
        </div>

        <div className="text-center mb-6">
          <p className="text-gray-700 text-sm sm:text-base mb-2">
            <span className="font-medium">
              The simplest way to track expenses:
            </span>{" "}
            Forward your receipt emails to{" "}
            <span className="text-blue-600 font-semibold">
              receipts@expensebot.ai
            </span>{" "}
            and we'll auto-process them!
          </p>
          <p className="text-gray-600 text-xs sm:text-sm">
            Tip: You can add a second email alias in <strong>Settings</strong>{" "}
            so that you can forward receipts from another email account.
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          <button
            onClick={onClose}
            className="w-full py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium text-sm sm:text-base"
          >
            <List className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
            Explore More Import Options
          </button>
        </div>

        <div className="mt-4 sm:mt-6 flex flex-wrap items-center justify-between gap-2">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={handleCheckboxChange}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-xs sm:text-sm text-gray-600">
              Don't show again
            </span>
          </label>
          <button
            onClick={handleClose}
            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs sm:text-sm"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
